import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FiX, FiSend } from "react-icons/fi";
import { publicAPI } from "../../api";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { getImageUrl } from "../../utils/helpers";

/* ===== Enquiry Popup Form ===== */
export const EnquiryPopup = () => {
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const location = useLocation();

  useEffect(() => {
    // Don't show on admin pages
    if (location.pathname.startsWith("/admin")) return;

    // Check if already dismissed this session
    const dismissed = sessionStorage.getItem("pt_enquiry_dismissed");
    if (dismissed) return;

    // Show after 3 seconds
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleClose = () => {
    setVisible(false);
    sessionStorage.setItem("pt_enquiry_dismissed", "true");
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) return;
    setLoading(true);
    try {
      await publicAPI.submitEnquiry({
        name: form.name,
        phone: form.phone,
        email: form.email,
        message: form.message,
        subject: "Website Enquiry",
      });
      setSubmitted(true);
      setTimeout(() => {
        handleClose();
      }, 2500);
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="enquiry-popup__overlay" onClick={handleClose}>
      <div className="enquiry-popup" onClick={(e) => e.stopPropagation()}>
        <button className="enquiry-popup__close" onClick={handleClose}>
          <FiX />
        </button>

        <div className="enquiry-popup__header">
          <h3>Get in Touch</h3>
          <p>Kindly let us know the branch you are interested in.</p>
        </div>

        {submitted ? (
          <div className="enquiry-popup__success">
            <div className="enquiry-popup__success-icon">✓</div>
            <h4>Thank you!</h4>
            <p>We'll get back to you shortly.</p>
          </div>
        ) : (
          <form className="enquiry-popup__form" onSubmit={handleSubmit}>
            <div className="enquiry-popup__field">
              <input
                type="text"
                name="name"
                placeholder="Your Name *"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="enquiry-popup__field">
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number *"
                value={form.phone}
                onChange={handleChange}
                required
              />
            </div>
            <div className="enquiry-popup__field">
              <input
                type="email"
                name="email"
                placeholder="Email Address *"
                value={form.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="enquiry-popup__field">
              <textarea
                name="message"
                placeholder="Your Message *"
                value={form.message}
                onChange={handleChange}
                rows={3}
                required
              />
            </div>
            <button
              type="submit"
              className="enquiry-popup__submit"
              disabled={loading}
            >
              {loading ? (
                "Sending..."
              ) : (
                <>
                  Send Message <FiSend />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ===== Floating WhatsApp Button ===== */
export const WhatsAppFloat = () => {
  const { settings, loaded } = useSiteSettings();
  const [pulse, setPulse] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const t = setTimeout(() => setPulse(false), 10000);
    return () => clearTimeout(t);
  }, []);

  if (!loaded || location.pathname.startsWith("/admin")) return null;

  const waNumber = settings.whatsapp_number || "";
  const waMessage = settings.whatsapp_message || "";

  if (!waNumber) return null;

  const url = `https://wa.me/${waNumber}${waMessage ? `?text=${encodeURIComponent(waMessage)}` : ""}`;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="wa-float"
      aria-label="Chat on WhatsApp"
    >
      {pulse && <span className="wa-float__pulse" />}
      <svg viewBox="0 0 175.216 175.552" className="wa-float__icon">
        <defs>
          <linearGradient
            id="wa-grad"
            x1="85.915"
            x2="86.535"
            y1="32.567"
            y2="137.092"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#57d163" />
            <stop offset="1" stopColor="#23b33a" />
          </linearGradient>
        </defs>
        <path
          d="M87.184 14.2A73.14 73.14 0 0014.63 87.508a72.54 72.54 0 009.94 36.59L14.2 161.35l38.2-10.15a72.78 72.78 0 0034.69 8.83h.03A73.16 73.16 0 00160.3 86.87 73.2 73.2 0 0087.184 14.2zm42.74 103.4c-1.8 5.05-10.48 9.65-14.65 10.27-3.77.56-8.55.8-13.8-1.34a104.6 104.6 0 01-12.52-5.55c-22.02-11.23-36.4-33.6-37.5-35.17-1.1-1.56-9.1-12.16-9.1-23.2 0-11.02 5.73-16.43 7.78-18.68a8.17 8.17 0 016-2.81c1.5 0 3 .02 4.3.07 1.38.06 3.23-.53 5.05 3.87 1.87 4.53 6.36 15.57 6.92 16.7.56 1.12.93 2.43.18 3.93-.74 1.5-1.12 2.43-2.24 3.74-1.12 1.3-2.35 2.92-3.36 3.92-1.12 1.1-2.28 2.3-1 4.48 1.3 2.18 5.75 9.5 12.35 15.4 8.48 7.54 15.63 9.88 17.84 10.98 2.2 1.1 3.5.93 4.78-.56 1.3-1.5 5.54-6.48 7.02-8.7 1.48-2.24 2.96-1.87 4.98-1.12 2.03.74 12.86 6.08 15.06 7.18 2.2 1.1 3.68 1.68 4.22 2.56.56.93.56 5.24-1.24 10.3z"
          fill="#fff"
        />
      </svg>
    </a>
  );
};

/* ===== Scroll To Top Button ===== */
export const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (location.pathname.startsWith("/admin")) return null;
  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="scroll-to-top"
      aria-label="Scroll to top"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="18 15 12 9 6 15" />
      </svg>
    </button>
  );
};

/* ===== Announcement Popup ===== */
export const AnnouncementPopup = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    publicAPI
      .getActiveAnnouncements()
      .then((r) => {
        const data = r.data.data || [];
        if (data.length > 0) setAnnouncements(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (announcements.length === 0) return;

    // Check if user has dismissed this session
    const dismissed = JSON.parse(
      sessionStorage.getItem("pt_dismissed_announcements") || "[]",
    );
    const toShow = announcements.find((a) => !dismissed.includes(a.id));
    if (!toShow) return;

    setCurrent(toShow);
    const timer = setTimeout(
      () => setVisible(true),
      (toShow.delay_seconds || 3) * 1000,
    );
    return () => clearTimeout(timer);
  }, [announcements]);

  const handleClose = () => {
    setVisible(false);
    if (current) {
      const dismissed = JSON.parse(
        sessionStorage.getItem("pt_dismissed_announcements") || "[]",
      );
      dismissed.push(current.id);
      sessionStorage.setItem(
        "pt_dismissed_announcements",
        JSON.stringify(dismissed),
      );
    }
  };

  if (!visible || !current) return null;

  const content = (
    <div className="announce-popup__img-wrap">
      <img loading="lazy" decoding="async"
        src={getImageUrl(current.image_url)}
        alt={current.title}
        className="announce-popup__img"
      />
    </div>
  );

  return (
    <div className="announce-popup__overlay" onClick={handleClose}>
      <div className="announce-popup" onClick={(e) => e.stopPropagation()}>
        <button className="announce-popup__close" onClick={handleClose}>
          <FiX />
        </button>
        {current.link_url ? (
          <a
            href={current.link_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
          >
            {content}
          </a>
        ) : (
          content
        )}
      </div>
    </div>
  );
};
